
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import type { ReCAPTCHA as ReCAPTCHAInstance } from 'react-google-recaptcha';

export default function Join() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    college: '',
    course: '',
    graduationYear: '',
    currentCity: '',
    interests: [] as string[],
    consent: false
  });

  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const interestOptions = [
    'Mentorship',
    'Events',
    'Volunteering',
    'CAMentrepreneurs'
  ];

  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const recaptchaRef = useRef<ReCAPTCHAInstance | null>(null);
  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    if (token) {
      setCaptchaError(null);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.consent) newErrors.consent = 'You must agree to be contacted';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInterestChange = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!captchaToken) {
      setCaptchaError('Please confirm you are not a robot.');
      return;
    }
    
    setFormStatus('submitting');
    
    try {
      const response = await fetch('/api/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          college: formData.college,
          course: formData.course,
          graduationYear: formData.graduationYear,
          currentCity: formData.currentCity,
          interests: formData.interests,
          consent: formData.consent,
          recaptchaToken: captchaToken
        })
      });

      const result = await response.json().catch(() => null);

      if (response.ok && result?.success) {
        setFormStatus('success');
        setFormData({
          fullName: '',
          email: '',
          college: '',
          course: '',
          graduationYear: '',
          currentCity: '',
          interests: [],
          consent: false
        });
        setErrors({});
        setCaptchaError(null);
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      } else {
        if (result?.errors?.recaptcha) {
          setCaptchaError(result.errors.recaptcha);
        }
        if (result?.errors) setErrors(result.errors);
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <section id="join" className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Join Our Community
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-4">
            Connect with fellow Cambridge alumni, access exclusive events, find mentorship opportunities,
            and contribute to meaningful social impact initiatives across Türkiye.
          </p>
          <div className="flex justify-center items-center gap-2 mt-6">
            <a
              href="https://www.linkedin.com/groups/9880223/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-teal-700 hover:text-teal-800 font-medium transition-colors"
            >
              <i className="ri-linkedin-box-fill text-2xl"></i>
              <span>Join our LinkedIn Group to stay connected</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" style={{ fontFamily: 'Georgia, serif' }}>
            Alumni Registration
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    errors.fullName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                />
                {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="your.email@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="college" className="block text-sm font-medium text-gray-700 mb-2">
                    Cambridge College
                  </label>
                  <input
                    type="text"
                    id="college"
                    name="college"
                    value={formData.college}
                    onChange={(e) => setFormData(prev => ({ ...prev, college: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="e.g., Trinity, King's, etc."
                  />
                </div>

                <div>
                  <label htmlFor="course" className="block text-sm font-medium text-gray-700 mb-2">
                    Course/Subject
                  </label>
                  <input
                    type="text"
                    id="course"
                    name="course"
                    value={formData.course}
                    onChange={(e) => setFormData(prev => ({ ...prev, course: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="e.g., Economics, Engineering"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="graduationYear" className="block text-sm font-medium text-gray-700 mb-2">
                    Graduation Year
                  </label>
                  <input
                    type="number"
                    id="graduationYear"
                    name="graduationYear"
                    value={formData.graduationYear}
                    onChange={(e) => setFormData(prev => ({ ...prev, graduationYear: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="2020"
                    min="1950"
                    max="2030"
                  />
                </div>

                <div>
                  <label htmlFor="currentCity" className="block text-sm font-medium text-gray-700 mb-2">
                    Current City
                  </label>
                  <input
                    type="text"
                    id="currentCity"
                    name="currentCity"
                    value={formData.currentCity}
                    onChange={(e) => setFormData(prev => ({ ...prev, currentCity: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Istanbul, Ankara, etc."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Areas of Interest
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {interestOptions.map((interest) => (
                    <label key={interest} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="interests"
                        checked={formData.interests.includes(interest)}
                        onChange={() => handleInterestChange(interest)}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{interest}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={(e) => setFormData(prev => ({ ...prev, consent: e.target.checked }))}
                    className={`w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mt-1 ${
                      errors.consent ? 'border-red-500' : ''
                    }`}
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    I agree to be contacted and to the privacy notice. *
                  </span>
                </label>
                {errors.consent && <p className="text-red-500 text-sm mt-1">{errors.consent}</p>}
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white py-3 px-6 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'submitting' ? 'Submitting...' : 'Join Community'}
              </button>

              <div className="flex justify-center">
                {recaptchaSiteKey ? (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    onChange={handleCaptchaChange}
                    onExpired={() => {
                      setCaptchaToken(null);
                      setCaptchaError('reCAPTCHA expired. Please try again.');
                    }}
                  />
                ) : (
                  <p className="text-red-500 text-sm text-center">
                    reCAPTCHA site key is not configured.
                  </p>
                )}
              </div>
              {captchaError && (
                <p className="text-red-500 text-sm text-center">{captchaError}</p>
              )}

              {formStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 text-sm">
                    Thank you for joining! We'll be in touch soon with more information.
                  </p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">
                    There was an error submitting your form. Please try again.
                  </p>
                </div>
              )}
            </form>
          </div>
      </div>
    </section>
  );
}
